import { Link, NavLink } from "react-router-dom";
import { Sparkles } from "lucide-react";

export function BrandMark({ size = 32 }: { size?: number }) {
  const s = `${size}px`;
  const inner = Math.round(size * 0.5);
  return (
    <div
      className="flex items-center justify-center rounded-lg bg-brand-gradient text-white shadow-sm"
      style={{ width: s, height: s }}
    >
      <svg
        width={inner}
        height={inner}
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M3 8 L 8 3 L 13 8 L 8 13 Z" fill="white" fillOpacity="0.95" />
        <circle cx="8" cy="8" r="1.6" fill="rgba(91,108,243,1)" />
      </svg>
    </div>
  );
}

export function LandingHeader() {
  return (
    <header className="sticky top-0 z-30 border-b border-ink-200/60 bg-white/80 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
        <Link to="/" className="flex items-center gap-2.5 no-underline">
          <BrandMark />
          <span className="text-lg font-semibold tracking-tight text-ink-900">
            BrandEcho
          </span>
        </Link>
        <nav className="hidden items-center gap-1 sm:flex">
          <HeaderLink to="/features">Features</HeaderLink>
          <HeaderLink to="/pricing">Pricing</HeaderLink>
        </nav>
        <div className="flex items-center gap-2">
          <Link
            to="/login"
            className="hidden rounded-md px-3 py-1.5 text-sm font-medium text-ink-700 no-underline transition-colors hover:bg-ink-100 hover:text-ink-900 sm:inline-flex"
          >
            Sign in
          </Link>
          <Link to="/login" className="btn-brand no-underline">
            Start free
          </Link>
        </div>
      </div>
    </header>
  );
}

function HeaderLink({
  to,
  children,
}: {
  to: string;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `rounded-md px-3 py-1.5 text-sm font-medium no-underline transition-colors ${
          isActive
            ? "bg-ink-100 text-ink-900"
            : "text-ink-600 hover:bg-ink-100 hover:text-ink-900"
        }`
      }
    >
      {children}
    </NavLink>
  );
}

export function LandingFooter() {
  const year = new Date().getFullYear();
  return (
    <footer className="border-t border-ink-200/70 bg-white">
      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
        <div className="grid gap-8 md:grid-cols-4">
          <div className="md:col-span-2">
            <Link to="/" className="flex items-center gap-2.5 no-underline">
              <BrandMark size={32} />
              <span className="text-base font-semibold tracking-tight text-ink-900">
                BrandEcho
              </span>
            </Link>
            <p className="mt-3 max-w-sm text-sm leading-relaxed text-ink-600">
              Voice-aware AI that turns one long-form post into native variants
              for X, Bluesky, LinkedIn, Newsletter, and short-video — and
              schedules them for you.
            </p>
            <div className="mt-4 flex items-center gap-2 text-xs text-ink-400">
              <Sparkles className="h-3.5 w-3.5" />
              Built on Cloudflare · Workers · D1 · R2
            </div>
          </div>

          <FooterCol
            title="Product"
            links={[
              { to: "/features", label: "Features" },
              { to: "/pricing", label: "Pricing" },
              { to: "/login", label: "Sign in" },
              { to: "/login", label: "Start free" },
            ]}
          />
          <FooterCol
            title="Resources"
            links={[
              { to: "/features", label: "Module guide" },
              { to: "/features#golden-path", label: "Golden-path tour" },
              { to: "/pricing", label: "Compare plans" },
            ]}
          />
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-between gap-3 border-t border-ink-100 pt-6 text-xs text-ink-500">
          <p>© {year} BrandEcho. Crafted for creators who write once.</p>
          <div className="flex items-center gap-3">
            <Link
              to="/features"
              className="text-ink-500 no-underline hover:text-ink-900"
            >
              Module guide
            </Link>
            <span className="text-ink-300">·</span>
            <Link
              to="/pricing"
              className="text-ink-500 no-underline hover:text-ink-900"
            >
              Pricing
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({
  title,
  links,
}: {
  title: string;
  links: { to: string; label: string }[];
}) {
  return (
    <div>
      <p className="text-2xs font-semibold uppercase tracking-wider text-ink-400">
        {title}
      </p>
      <ul className="mt-3 space-y-2">
        {links.map((l) => (
          <li key={l.label}>
            <Link
              to={l.to}
              className="text-sm text-ink-600 no-underline transition-colors hover:text-ink-900"
            >
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
